import React, { useState, useEffect } from 'react';

const font = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const C = {
  blue: '#1a56db', bg: '#f0f2f5', white: '#ffffff',
  gray2: '#f3f4f6', gray3: '#e5e7eb', gray5: '#6b7280',
  gray7: '#111827', green: '#16a34a', greenBg: '#dcfce7',
  red: '#dc2626', redBg: '#fee2e2', yellow: '#d97706', yellowBg: '#fef3c7',
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export default function PushPage() {
  const [status,   setStatus]   = useState('idle'); // idle | requesting | granted | denied | unsupported | subscribed
  const [error,    setError]    = useState(null);
  const [isIOS,    setIsIOS]    = useState(false);
  const [isPWA,    setIsPWA]    = useState(false);

  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const pwa = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsIOS(ios);
    setIsPWA(pwa);

    // Check if already subscribed
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => {
          if (sub) setStatus('subscribed');
        });
      });
    } else {
      setStatus('unsupported');
    }
  }, []);

  async function subscribe() {
    setStatus('requesting');
    setError(null);
    try {
      // Register service worker
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID key
      const keyRes = await fetch('/api/push/vapid-public-key');
      const { key } = await keyRes.json();

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus('denied');
        return;
      }

      // Subscribe
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });

      // Send to server
      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub.toJSON()),
      });

      setStatus('subscribed');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  }

  async function unsubscribe() {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch('/api/push/unsubscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setStatus('idle');
    } catch (err) {
      setError(err.message);
    }
  }

  const card = { background: C.white, borderRadius: 16, padding: '28px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', maxWidth: 480, width: '100%' };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: font }}>
      <div style={card}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>MessageBeacon</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.gray7, margin: '0 0 8px' }}>Push Notifications</h1>
          <p style={{ fontSize: 14, color: C.gray5, margin: 0, lineHeight: 1.6 }}>
            We are testing push notifications. Enable them below to receive alerts from your MessageBeacon directly on this device.
          </p>
        </div>

        {/* iOS not in PWA warning */}
        {isIOS && !isPWA && (
          <div style={{ background: C.yellowBg, border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.yellow, marginBottom: 4 }}>iOS requires Home Screen installation</div>
            <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
              To receive push notifications on iPhone or iPad, tap the <strong>Share</strong> button in Safari, then select <strong>Add to Home Screen</strong>. Then reopen this page from your home screen.
            </div>
          </div>
        )}

        {/* Unsupported */}
        {status === 'unsupported' && (
          <div style={{ background: C.redBg, border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 13, color: C.red }}>Push notifications are not supported in this browser.</div>
          </div>
        )}

        {/* Denied */}
        {status === 'denied' && (
          <div style={{ background: C.redBg, border: '1px solid #fecaca', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.red, marginBottom: 4 }}>Notifications blocked</div>
            <div style={{ fontSize: 12, color: '#991b1b', lineHeight: 1.5 }}>You've blocked notifications for this site. To enable them, go to your browser settings and allow notifications for this site, then reload.</div>
          </div>
        )}

        {/* Subscribed */}
        {status === 'subscribed' && (
          <div style={{ background: C.greenBg, border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 20 }}>✅</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.green }}>Notifications enabled</div>
              <div style={{ fontSize: 12, color: '#166534' }}>This device will receive MessageBeacon push notifications.</div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: C.redBg, border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: C.red }}>
            {error}
          </div>
        )}

        {/* Action button */}
        {status !== 'unsupported' && (
          <div style={{ display: 'flex', gap: 10 }}>
            {status !== 'subscribed' ? (
              <button
                onClick={subscribe}
                disabled={status === 'requesting' || (isIOS && !isPWA)}
                style={{
                  flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 600,
                  background: (isIOS && !isPWA) ? C.gray3 : C.blue,
                  color: (isIOS && !isPWA) ? C.gray5 : '#fff',
                  border: 'none', borderRadius: 10, cursor: (isIOS && !isPWA) ? 'not-allowed' : 'pointer',
                  fontFamily: font,
                }}
              >
                {status === 'requesting' ? 'Requesting…' : 'Enable notifications'}
              </button>
            ) : (
              <button
                onClick={unsubscribe}
                style={{ flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 500, background: C.gray2, color: C.gray5, border: '1px solid ' + C.gray3, borderRadius: 10, cursor: 'pointer', fontFamily: font }}
              >
                Disable notifications
              </button>
            )}
          </div>
        )}

        <div style={{ marginTop: 20, fontSize: 11, color: C.gray5, textAlign: 'center' }}>
          MessageBeacon · Testing mode
        </div>
      </div>
    </div>
  );
}
